import React, { useEffect, useLayoutEffect, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { clearAfterExpiry, isKarkhanaSupervisor } from "../../../helpers/karkhanaEnabled";
import { useKarkhanaPwa } from "../../../helpers/useCollectorPwa";
import { useKT } from "../i18n/kt";
import { hasRememberedPhone, renewOnce, tokenNeedsRenewing } from "../supervisor/supervisorSession";
import "../ui/karkhana-ui.scss";
import "./karkhana-frame.scss";
import { karkhanaPageFor, karkhanaTitleFor, kp } from "../karkhanaPaths";
import { clearReturnTrail } from "../../../helpers/returnTrail";
import { useCashbookStatus } from "../../../helpers/cashbookStatus";
import ReminderStrip from "../cashbook/supervisor/ReminderStrip";
import { startPhotoQueue } from "../cashbook/photoQueue";

/** The tab title outside Karkhana (index.html), put back when someone leaves it. */
const OUTSIDE_TITLE = typeof document !== "undefined" ? document.title : "";

const readRole = (): string | undefined => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "null")?.users?.role;
  } catch {
    return undefined;
  }
};

const ICON: Record<string, React.ReactNode> = {
  home: <path d="M3 11l9-7 9 7v9a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1z" />,
  work: (
    <>
      <rect x="3" y="7" width="18" height="10" rx="2" />
      <path d="M7 7v4M11 7v3M15 7v4M19 7v3" />
    </>
  ),
  att: (
    <>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2.5 20c.8-3.5 3.4-5.5 6.5-5.5s5.7 2 6.5 5.5" />
      <path d="M16 11l2 2 4-4" />
    </>
  ),
  pay: (
    <>
      <rect x="2.5" y="6" width="19" height="13" rx="2" />
      <circle cx="12" cy="12.5" r="2.5" />
    </>
  ),
  hkp: (
    <>
      <rect x="3.5" y="3.5" width="7" height="7" rx="1.5" />
      <rect x="13.5" y="3.5" width="7" height="7" rx="1.5" />
      <rect x="3.5" y="13.5" width="7" height="7" rx="1.5" />
      <rect x="13.5" y="13.5" width="7" height="7" rx="1.5" />
    </>
  ),
  // Cash book (CASHBOOK add-on): a stack of notes with ₹.
  cash: (
    <>
      <rect x="2.5" y="8" width="17" height="11" rx="1.8" />
      <path d="M5 5.5h15.5a1 1 0 0 1 1 1V16" />
      <path d="M8.5 11.2h5M8.5 13.4h5M10.2 11.2c1.9 0 1.9 2.2 0 2.2l3 3" />
    </>
  ),
  more: (
    <>
      <circle cx="5" cy="12" r="1.6" />
      <circle cx="12" cy="12" r="1.6" />
      <circle cx="19" cy="12" r="1.6" />
    </>
  ),
};

const Icon = ({ name }: { name: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {ICON[name]}
  </svg>
);

type Tab = { key: string; to: string; label: string; match: (p: string) => boolean };

const isHome = (p: string) => p === kp.home() || p === `${kp.home()}/`;
const TABS: Tab[] = [
  { key: "home", to: kp.home(), label: "Home", match: isHome },
  { key: "work", to: kp.dailyWork(), label: "Daily work", match: (p) => p.startsWith(kp.dailyWork()) || p.startsWith(kp.rounds()) },
  { key: "att", to: kp.attendance(), label: "Attendance", match: (p) => p.startsWith(kp.attendance()) || p.startsWith(kp.jobWork()) },
  { key: "pay", to: kp.pay(), label: "Pay", match: (p) => p.startsWith(kp.pay()) || p.startsWith(kp.cashBook()) },
];

/**
 * A supervisor's tabs when the studio has the Cash book add-on (V1_SPEC D32, 11.3): रोज़ का काम ·
 * हाज़िरी · भुगतान · नकद · और. Home (the loom board) moves into More. Pay matches pay and pay sheets only.
 */
const CASH_TABS: Tab[] = [
  TABS[1],
  TABS[2],
  { key: "pay", to: kp.pay(), label: "Pay", match: (p) => p.startsWith(kp.pay()) },
  { key: "cash", to: kp.cashBook(), label: "Cash", match: (p) => p.startsWith(kp.cashBook()) },
];

/** The tabs for this person (supervisor + add-on → CASH_TABS; everyone else exactly as before). */
export const tabsFor = (supervisor: boolean, cashbook: boolean): Tab[] => (supervisor && cashbook ? CASH_TABS : TABS);

/** The red dot on नकद: photos missing + handovers to answer + handovers refused (V1_SPEC 11.3). */
export const cashDotCount = (badge: { missing: number; incomingTransfers: number; rejectedTransfers: number }) =>
  badge.missing + badge.incomingTransfers + badge.rejectedTransfers;

/** Everything set up once or used now and then: the desktop sidebar shows it open, phones use the More tab. */
export const MORE_GROUPS: { title: string; staffOnly?: boolean; items: { to: string; label: string; staffOnly?: boolean }[] }[] = [
  {
    title: "Sarees & money",
    items: [
      { to: kp.sarees(), label: "Sarees" },
      { to: kp.paySheets(), label: "Pay sheets" },
      { to: kp.cashBook(), label: "Cash book" },
      { to: kp.approvals(), label: "Approvals" },
      { to: kp.costings(), label: "Costing", staffOnly: true },
      { to: kp.reports(), label: "Reports", staffOnly: true },
    ],
  },
  {
    title: "People",
    staffOnly: true,
    items: [
      { to: kp.workers(), label: "Workers" },
      { to: kp.import(), label: "Import" },
      { to: kp.workerPhotos(), label: "Take photos" },
      { to: kp.supervisors(), label: "Supervisors" },
      { to: kp.codes(), label: "Work types & codes" },
    ],
  },
  {
    title: "Designs & rates",
    staffOnly: true,
    items: [
      { to: kp.designs(), label: "Designs" },
      { to: kp.rateCards(), label: "Rate cards" },
      { to: kp.timeRates(), label: "Time rates" },
      { to: kp.parts(), label: "Saree parts" },
      { to: kp.costingLines(), label: "Costing lines" },
    ],
  },
  {
    title: "Imports & help",
    staffOnly: true,
    items: [
      { to: kp.rateImport(), label: "Import designs & rates" },
      { to: kp.oldSheet(), label: "Old DAILY WORK sheet" },
      { to: kp.guide(), label: "How it works" },
    ],
  },
];

/**
 * Karkhana's own navigation around every Karkhana page: a tab bar at the bottom of a phone, a rail
 * on a tablet, a sidebar with More open on a desktop. Daily work and Attendance are the two places
 * work is entered; everything else is one tap away in More.
 */
const KarkhanaFrame = ({ children }: { children: React.ReactNode }) => {
  const { t } = useKT();
  const { pathname } = useLocation();
  const supervisor = isKarkhanaSupervisor(readRole());
  const history = useHistory();
  // The browser tab says where you are ("Rounds · Floor · Karkhana"). A layout effect, so a detail
  // page's own name (useKarkhanaTitle, a normal effect) lands after it.
  useLayoutEffect(() => {
    document.title = karkhanaPageFor(pathname) ? karkhanaTitleFor(pathname) : OUTSIDE_TITLE || document.title;
  }, [pathname]);
  useEffect(
    () => () => {
      // Leaving for a page that is not a signed-in Karkhana page (incl. /karkhana/login): put the title back.
      if (OUTSIDE_TITLE && !karkhanaPageFor(history.location.pathname)) document.title = OUTSIDE_TITLE;
    },
    [history]
  );
  // Back inside Karkhana: a trail left for a product or looms page has done its job.
  useEffect(() => clearReturnTrail(), []);
  // Supervisors can add Karkhana to the home screen, and a remembered phone keeps its login fresh.
  useKarkhanaPwa(supervisor);
  // An expired login is renewed before the page loads its data; one ending soon is renewed alongside.
  const [ready, setReady] = useState(() => !(supervisor && hasRememberedPhone() && tokenNeedsRenewing(Date.now(), 0)));
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!supervisor || !hasRememberedPhone() || !tokenNeedsRenewing()) return;
    renewOnce().then((ok) => {
      // The studio signed this phone out and the login has run out: straight to the PIN, keeping language and mobile.
      if (!ok && tokenNeedsRenewing(Date.now(), 0)) {
        if (!hasRememberedPhone()) {
          // The login ran out (not a logout): unsaved Daily work stays for this supervisor's next sign-in.
          clearAfterExpiry(true);
          history.replace(kp.login());
        } else {
          // Offline, or Karkhana is off for the studio: say so instead of opening pages that would fail.
          setFailed(true);
        }
        return;
      }
      setReady(true);
    });
  }, [supervisor, history]);

  const cash = useCashbookStatus(ready);
  const cashOn = cash.loaded && cash.enabled;
  // Photos taken offline go as soon as the app opens or the network is back, on any Karkhana page.
  useEffect(() => {
    if (supervisor && cashOn) startPhotoQueue();
  }, [supervisor, cashOn]);
  const baseTabs = tabsFor(supervisor, cashOn);
  const active = baseTabs.find((tab) => tab.match(pathname))?.key || "more";
  const tabs = [...baseTabs, { key: "more", to: kp.more(), label: "More", match: () => false }];
  const dot = cashOn ? cashDotCount(cash.badge) : 0;
  const toCheck = cashOn && !supervisor ? cash.badge.toCheck : 0;
  const groups = MORE_GROUPS.filter((g) => !(supervisor && g.staffOnly));
  // A supervisor with the add-on reaches the loom board from More; the cash book is a tab of its own.
  const navGroups =
    supervisor && cashOn
      ? groups.map((g, i) => ({
          ...g,
          items: [...(i === 0 ? [{ to: kp.home(), label: "Home" }] : []), ...g.items.filter((it) => it.to !== kp.cashBook())],
        }))
      : groups;
  return (
    <div className="kf">
      <nav className="kf-nav" aria-label={t("Karkhana")}>
        <div className="kf-nav__brand">{t("Karkhana")}</div>
        {tabs.map((tab) => (
          <Link
            key={tab.key}
            to={tab.to}
            className={`kf-nav__tab${active === tab.key ? " is-on" : ""}${tab.key === "more" ? " kf-nav__tab--more" : ""}`}
            aria-current={active === tab.key ? "page" : undefined}
          >
            <span className="kf-nav__icon">
              <Icon name={tab.key} />
              {tab.key === "cash" && dot > 0 ? (
                <em className="kf-dot" aria-label={t("{{n}} to do", { n: dot })}>
                  {dot > 99 ? "99+" : dot}
                </em>
              ) : null}
            </span>
            <span>{t(tab.label)}</span>
          </Link>
        ))}
        {supervisor ? null : (
          <Link to="/dashboard" className="kf-nav__tab kf-nav__hkp">
            <Icon name="hkp" />
            <span>{t("All of HKP")}</span>
          </Link>
        )}
        <div className="kf-nav__groups">
          {navGroups.map((g) => (
            <div key={g.title} className="kf-nav__group">
              <div className="kf-nav__grouptitle">{t(g.title)}</div>
              {g.items
                .filter((i) => !(supervisor && i.staffOnly))
                .map((i) => (
                  <Link key={i.to} to={i.to} className={`kf-nav__sub${pathname === i.to ? " is-on" : ""}`}>
                    {t(i.label)}
                    {i.to === kp.cashBook() && toCheck > 0 ? (
                      <em className="kf-count" aria-label={t("{{n}} to check", { n: toCheck })}>
                        {toCheck}
                      </em>
                    ) : null}
                  </Link>
                ))}
            </div>
          ))}
        </div>
      </nav>
      <div className="kf-main">
        {ready ? (
          <>
            {supervisor && cashOn && cash.badge.missing > 0 && pathname.startsWith(kp.dailyWork()) ? <ReminderStrip /> : null}
            {children}
          </>
        ) : failed ? (
          <div className="kf-opening" role="alert">
            <p>{t("Could not open Karkhana. Check the internet, or ask the studio admin if it keeps happening.")}</p>
            <button type="button" className="kh-btn is-primary" onClick={() => window.location.reload()}>
              {t("Try again")}
            </button>
          </div>
        ) : (
          <p className="kf-opening" role="status">
            {t("Opening…")}
          </p>
        )}
      </div>
    </div>
  );
};

export default KarkhanaFrame;
